/**
 * THE WORDS FOR CREATING ACCEPTANCE LINKS IN BULK — one place, so the window cannot invent any.
 *
 * The server decides everything: which records may have a link, what it costs, and what happened.
 * This module only names those answers in Turkish, and it is the browser's half of
 * `tests/fixtures/bulk-claim-link-plan.json` — the same arrangement `bulk-send.ts` and
 * `first-send.ts` carry for the send side, and for the same reason: a count the operator approves
 * and a sentence explaining it must not be able to drift apart from the rule that produced them.
 *
 * TWO RULES THIS FILE OBEYS, both of them the fixture's own:
 *
 *   NOTHING HERE SAYS A LINK WAS «verildi» OR «alındı». This act produces a link and seals it; who
 *   reads it afterwards is not something the product can observe, and no sentence claims otherwise.
 *
 *   NO SENTENCE NAMES AN ADDRESS. The act's answer carries no link at all — not even for the
 *   records it minted — because the links are readable only through the authorized CSV download.
 */

/** Why a record gets no link. The server's own names, unchanged. */
export const BULK_CLAIM_LINK_SKIP_TEXT: Readonly<Record<string, string>> = {
  awaiting_admin_publish: 'Kayıt yönetici yayını bekliyor; kabul bağlantısı üretilmez.',
  declined: 'Alıcı sertifikayı reddetti; kabul bağlantısı üretilmez.',
  revoked: 'Kayıt iptal edildi; kabul bağlantısı üretilmez.',
  superseded: 'Kayıt yenisiyle değiştirildi; kabul bağlantısı üretilmez.',
  expired: 'Kaydın geçerliliği sona erdi; kabul bağlantısı üretilmez.',
  already_accepted: 'Alıcı sertifikayı kabul etti; kabul bağlantısı üretilmez.',
  no_acceptance_needed: 'Bu kayıt kabul istemiyor; kabul bağlantısı üretilmez.',
  /* Facts about the run rather than about the record — see the fixture's `runSkips`. */
  changed_since_preview: 'Kayıt, onay verildikten sonra değişti; bu kayıt için hiçbir şey yapılmadı.',
  record_busy: 'Kayıt başka bir işlem tarafından kullanılıyordu; bu turda işlenmedi.',
  revocation_not_confirmed: 'Mevcut geçerli bağlantının iptali onaylanmadı; bağlantı değiştirilmedi.',
  unprotected_send_pending:
    'Bekleyen bir e-posta gönderimi bu bağlantıyı değiştirebilir; bağlantı üretilmedi.',
};

/**
 * A reason the server named, in the operator's language.
 *
 * An unrecognised code is shown as «İşleme alınmadı» and NOT as a success and not as an error: the
 * server's vocabulary can only grow through a deployment, and a sentence this build has never seen
 * is still a record that got no link. The raw code is never drawn on the screen.
 */
export const bulkClaimLinkReasonText = (reason: string): string =>
  BULK_CLAIM_LINK_SKIP_TEXT[reason] ?? 'İşleme alınmadı; bu kayıt için bağlantı üretilmedi.';

/** The two successes, named: one produced a link, the other kept the one the record had. */
export const BULK_CLAIM_LINK_OUTCOME_TEXT: Readonly<Record<string, string>> = {
  created: 'Yeni kabul bağlantısı üretildi ve şifreli saklandı.',
  reused: 'Kaydın mevcut kabul bağlantısı korundu; yeni bağlantı üretilmedi.',
  failed: 'Bu kayıt için işlem tamamlanamadı; hiçbir şey yazılmadı ve yeniden denenebilir.',
};

export const bulkClaimLinkOutcomeText = (outcome: string): string =>
  BULK_CLAIM_LINK_OUTCOME_TEXT[outcome] ?? bulkClaimLinkReasonText(outcome);

/**
 * THE SEVEN NUMBERS, in the order they are read.
 *
 * `revoking` last and on its own, because it is the only one that asks for a decision rather than
 * describing one: it is a subset of `needsNewLink` and it is what the separate approval is about.
 */
export const BULK_CLAIM_LINK_IMPACT_ROWS = [
  { key: 'selected', label: 'Seçilen sertifika' },
  { key: 'pendingAcceptance', label: 'Onay bekleyen' },
  { key: 'alreadyAccepted', label: 'Zaten kabul edilmiş' },
  { key: 'ineligible', label: 'İptal edilmiş veya uygun olmayan' },
  { key: 'reusable', label: 'Mevcut bağlantısı korunacak' },
  { key: 'needsNewLink', label: 'Yeni bağlantı üretilecek' },
  { key: 'revoking', label: 'Mevcut geçerli bağlantısı iptal edilecek' },
] as const;
export type BulkClaimLinkImpactKey = (typeof BULK_CLAIM_LINK_IMPACT_ROWS)[number]['key'];

/**
 * THE SENTENCE THE SEPARATE APPROVAL IS ABOUT, and it states the cost rather than softening it.
 *
 * Said in the plural form the count needs, and it says what cannot be known: whether anybody is
 * holding one of those links. The raw value was never stored, so the product cannot say — and the
 * honest sentence is the one that admits it.
 */
export const bulkClaimLinkRevocationWarning = (count: number): string =>
  `${count} kaydın şu an çalışan kabul bağlantısı iptal edilecek ve yerine yenisi üretilecek. ` +
  'Bu bağlantıların daha önce paylaşılıp paylaşılmadığı kayıtlardan belirlenemiyor; ' +
  'paylaşılmışsa eski bağlantı çalışmayı bırakır.';
